import React, { useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  AssignmentTurnedIn as OperationsIcon,
  AccountBalanceWallet as PaymentsIcon,
  Store as VendorsIcon,
  Speed as EfficiencyIcon,
  Lightbulb as InsightsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

interface SuggestionCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  prompts: string[];
  tooltip: string;
}

interface SuggestionChipsProps {
  onPromptClick: (prompt: string) => void;
  isLoading?: boolean;
  expandedCategory: string | null;
  setExpandedCategory: (category: string | null) => void;
}

const suggestionCategories: SuggestionCategory[] = [
  {
    id: 'operations',
    label: 'Operations',
    icon: <OperationsIcon />,
    tooltip: 'Get help with GL mappings, approval cycles',
    prompts: [
      'Which invoices are pending my approval?',
      'Show invoices missing GL codes that are most aged.',
      'Invoices flagged or at risk of late payment.',
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: <PaymentsIcon />,
    tooltip: 'Track payments, cash flow, and due dates',
    prompts: [
      'What payments are due this week?',
      'List invoices scheduled for payment.',
      'Impact on cashflow based on scheduled payment runs.',
    ],
  },
  {
    id: 'vendors',
    label: 'Vendors',
    icon: <VendorsIcon />,
    tooltip: 'Analyze vendor spend and relationships',
    prompts: [
      'Top vendors by spend this month.',
      'Top vendors awaiting action.',
      'New vendors added this quarter.',
    ],
  },
  {
    id: 'efficiency',
    label: 'Efficiency',
    icon: <EfficiencyIcon />,
    tooltip: 'Analyze bottlenecks, improve AP efficiency',
    prompts: [
      'Show vendor mapping automation trends for last full month.',
      'Approval cycle time by location for last full month.',
      'Which users are most active last full month, based on invoice edits and mapping?',
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: <InsightsIcon />,
    tooltip: 'Discover spending trends and patterns',
    prompts: [
      'Top-5 vendors by spend growth vs last full month.',
      'Highest spending location last quarter.',
      'Which vendors have increased prices by > 2% based on last 3 months, show me the top-3.',
    ],
  },
];

const SuggestionChips: React.FC<SuggestionChipsProps> = ({
  onPromptClick,
  isLoading,
  expandedCategory,
  setExpandedCategory,
}) => {
  const handleCategoryClick = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const handlePromptClick = (prompt: string) => {
    onPromptClick(prompt);
    setExpandedCategory(null); // Close expanded view after selection
  };

  return (
    <Box
      sx={{
        width: '100%',
        animation: 'fadeIn 0.4s ease-in',
        '@keyframes fadeIn': {
          '0%': {
            opacity: 0,
            transform: 'translateY(8px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      {/* Category chips - always show first */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          justifyContent: 'center',
          flexWrap: 'nowrap',
          mt: 1,
        }}
      >
        {suggestionCategories.map((category) => (
          <Chip
            key={category.id}
            icon={category.icon as any}
            label={category.label}
            title={category.tooltip}
            onClick={() => handleCategoryClick(category.id)}
            disabled={isLoading}
            sx={{
              backgroundColor: expandedCategory === category.id ? '#e8f9f5' : '#e8ecef',
              color: expandedCategory === category.id ? '#00796b' : '#4a5568',
              py: 2.5,
              px: 1.25,
              fontSize: '0.875rem',
              fontWeight: 450,
              border: '1.5px solid',
              borderColor: expandedCategory === category.id ? '#a6f0db' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              boxShadow: 'none',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '0',
                height: '0',
                borderRadius: '50%',
                backgroundColor: 'rgba(39, 196, 153, 0.1)',
                transform: 'translate(-50%, -50%)',
                transition: 'width 0.6s ease, height 0.6s ease',
              },
              '& .MuiChip-icon': {
                color: expandedCategory === category.id ? '#27c499' : '#718096',
                fontSize: '1.125rem',
                marginLeft: '4px',
                marginRight: '-2px',
                transition: 'color 0.25s ease',
              },
              '& .MuiChip-label': {
                paddingLeft: '8px',
                paddingRight: '8px',
                position: 'relative',
                zIndex: 1,
              },
              '&:hover': {
                backgroundColor: '#e8f9f5',
                borderColor: '#a6f0db',
                color: '#00796b',
                transform: 'scale(1.02)',
                boxShadow: '0 2px 12px rgba(39, 196, 153, 0.12)',
                '& .MuiChip-icon': {
                  color: '#27c499',
                },
              },
              '&:active': {
                transform: 'scale(0.98)',
                '&::before': {
                  width: '300px',
                  height: '300px',
                  transition: 'width 0.1s ease, height 0.1s ease',
                },
              },
              '&:focus': {
                outline: '2px solid #a6f0db',
                outlineOffset: '2px',
              },
              '&.Mui-disabled': {
                opacity: 0.5,
              },
            }}
          />
        ))}
      </Box>

      {/* Expanded prompts view - shows below category chips */}
      {expandedCategory !== null && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            mt: 1.5,
          }}
        >
          {suggestionCategories
            .find((c) => c.id === expandedCategory)
            ?.prompts.map((prompt, index) => (
              <Box
                key={index}
                component="button"
                onClick={() => handlePromptClick(prompt)}
                disabled={isLoading}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: 1,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.125rem' }} />
                <Typography
                  sx={{
                    fontSize: '0.9375rem',
                    color: 'text.primary',
                    lineHeight: 1.4,
                  }}
                >
                  {prompt}
                </Typography>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default SuggestionChips;